<template>
  <div>
    <div class="grid w-full grid-cols-2 gap-6 px-6">
      <div class="h-[67vh] w-full overflow-y-auto">
        <div class="flex w-full">
          <div class="group w-full">
            <div class="mb-3 flex items-center gap-3">
              <p class="text-body-default-m">{{ t("sender") }}</p>
              <Button
                v-if="canChangeSender"
                class="max-h-max"
                type="button"
                :variant="ButtonVariant.Ghost"
                @click="emit('changeStep', 0)"
                >{{ t("edit") }}</Button
              >
            </div>
            <div>
              <p class="text-body-default mb-1">
                {{ values.sender.business ? values.sender.company_name : values.sender.name }}
              </p>
              <p v-if="values.sender.business" class="text-body-default">
                {{ values.sender.name }}
              </p>
              <p class="text-body-default">{{ values.sender.street1 }}</p>
              <p class="text-body-default mb-1">{{ values.sender.street2 }}</p>
              <p class="text-body-default">{{ values.sender.zip_code }} {{ values.sender.city }}</p>
              <p class="text-body-default">{{ getCountryName(values.sender.country) }}</p>
            </div>
          </div>
          <Divider class="mx-6" rotation="vertical" />
          <div class="group w-full pb-3">
            <div class="mb-3 flex items-center gap-3">
              <p class="text-body-default-m">{{ t("receiver") }}</p>
              <Button
                v-if="canChangeReceiver"
                class="max-h-max"
                type="button"
                :variant="ButtonVariant.Ghost"
                @click="emit('changeStep', 0)"
                >{{ t("edit") }}</Button
              >
            </div>
            <p class="text-body-default mb-1">
              {{ values.receiver.business ? values.receiver.company_name : values.receiver.name }}
            </p>
            <p v-if="values.receiver.business" class="text-body-default">
              {{ values.receiver.name }}
            </p>
            <p class="text-body-default">{{ values.receiver.street1 }}</p>
            <p class="text-body-default">{{ values.receiver.street2 }}</p>
            <p class="text-body-default">{{ values.receiver.zip_code }} {{ values.receiver.city }}</p>
            <p class="text-body-default mb-1">
              {{ getCountryName(values.receiver.country) }}
            </p>
            <p class="text-body-default">{{ values.receiver.email }}</p>
            <p class="text-body-default">{{ values.receiver.phone }}</p>
          </div>
        </div>
        <Divider class="!mt-0 mb-3" />
        <div class="group w-full">
          <div class="mb-3 flex items-center gap-3">
            <p class="text-body-default-m">{{ t("size_and_weight") }}</p>
            <Button class="max-h-max" type="button" :variant="ButtonVariant.Ghost" @click="emit('changeStep', 1)">{{
              t("edit")
            }}</Button>
          </div>
          <div class="flex items-center gap-1">
            <p class="text-body-default-m">L:{{ values.length }} <span>cm</span></p>
            <Icon :size="ComponentSize.sm" src="X" />
            <p class="text-body-default-m">B:{{ values.width }} <span>cm</span></p>
            <Icon :size="ComponentSize.sm" src="X" />
            <p class="text-body-default-m">H:{{ values.height }} <span>cm</span></p>
            <Divider class="mx-3 h-5" rotation="vertical" />
            <p class="text-body-default-m">
              {{ useFormatWeight(values.weight, { withoutUnit: true }) }}
              <span>{{ usePreferredWeightUnit() }}</span>
            </p>
          </div>
        </div>
        <template v-if="values.set_order_lines">
          <Divider class="my-3" />
          <div class="w-full">
            <p class="text-body-default mb-3 font-medium">{{ t("orderline", 99) }}</p>
            <div v-for="(order_line, i) in values.set_order_lines" :key="i" class="mb-1.5 flex flex-wrap gap-1.5">
              <p class="text-body-default w-[30px]">{{ order_line.qty }}x</p>
              <div>
                <p class="text-body-default">
                  {{ order_line?.item_number }} {{ order_line?.customs[0]?.description }}
                  {{ t("weighing").toLocaleLowerCase() }}
                  {{ parseInt(order_line?.customs[0]?.weight) * order_line?.qty }}g
                  {{ t("costing").toLocaleLowerCase() }} {{ parseInt(order_line?.customs[0]?.total_price).toFixed(2) }}
                  {{ t("from").toLocaleLowerCase() }} {{ getCountryName(order_line?.customs[0]?.origin_country) }}
                </p>
                <Badge v-if="order_line?.dangerous_goods_set">{{ t("dangerous_goods") }}</Badge>
              </div>
            </div>
          </div>
        </template>
        <template v-if="carrierIsDfm">
          <Divider class="my-3" />
          <div class="w-full">
            <p class="text-body-default mb-3 font-medium">{{ t("pickup") }}</p>

            <label class="text-body-default text-left">{{ t("shipment_ready_for_pickup") }}</label>
            <div class="flex items-center gap-3">
              <div class="my-1 !max-w-[190px]">
                <Datepicker
                  ref="calenderRef"
                  class="filter-value my-1 w-full"
                  name="pickup_date"
                  :presets="[]"
                  :bottom-buttons="false"
                  :requires-confirmation="false"
                  calendar-icon
                />
              </div>
              <div class="flex items-center gap-1">
                <p class="text-body-default">Kl.</p>
                <InputTime
                  class="filter-value my-1 w-full max-w-max"
                  value-as-date
                  :minutes-interval="15"
                  name="pickup_time"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="flex h-[67vh] w-full flex-col overflow-hidden rounded-xl">
        <div class="bg-bgr-secondary mb-2 flex min-h-0 w-full flex-grow flex-col rounded-xl">
          <div class="overflow-auto px-3 py-3">
            <div class="group">
              <div class="mb-3 flex items-center gap-3">
                <p class="text-body-default-m">{{ t("product", 99) }}</p>
                <p v-if="carrierIsDfm" class="text-body-small leading-none">
                  {{ t("with_ev_and_oil") }}
                </p>
                <Button class="max-h-max" type="button" :variant="ButtonVariant.Ghost" @click="emit('changeStep', 2)">{{
                  t("edit")
                }}</Button>
              </div>
              <div class="flex items-center">
                <Avatar
                  :entity="{
                    image: getCarrierLogoUrl(carrier),
                  }"
                  :size="ComponentSize.lg"
                />
                <p class="text-body-default ml-3 font-medium">{{ values.carrier_product_title }}</p>
                <p class="text-body-default ml-auto font-medium">
                  {{ useFormatPrice(calculatedPrice ?? values.carrier_product_price, true, quoteCurrency) }}
                </p>
              </div>
            </div>
            <div v-if="!isSKYNET || !isDFM || (isDFM && values.carrier_product_service.split('_')[0] !== 'dfm')">
              <Divider class="!my-3" />
              <div class="group">
                <div class="mb-3 flex items-center gap-3">
                  <p class="text-body-default-m">Homerunner {{ t("insurance") }}</p>
                  <Button type="button" :variant="ButtonVariant.Ghost" @click="emit('changeStep', 7)">{{
                    t("edit")
                  }}</Button>
                </div>
                <div class="ml-3 flex">
                  <p v-if="values.insurance_coverage" class="text-body-default-m">
                    {{ t("up_to") }} {{ useFormatPrice(values.insurance_coverage, "auto", insuranceCurrency) }}
                  </p>
                  <p v-else class="text-body-default-m">
                    {{ t("up_to") }} {{ useFormatPrice(freeCoverage, "auto", freeCoverageCurrency) }}
                  </p>
                  <p class="text-body-default ml-auto font-medium">
                    {{ useFormatPrice(values.insurance_price, true, insuranceCurrency) || t("free") }}
                  </p>
                </div>
              </div>
            </div>
            <div v-if="extraServices">
              <Divider class="!my-3" />
              <div class="group">
                <div class="mb-3 flex gap-3">
                  <p class="text-body-default-m">{{ t("extra_services") }}</p>
                  <Button type="button" :variant="ButtonVariant.Ghost" @click="emit('changeStep', 4)">{{
                    t("edit")
                  }}</Button>
                  <p v-if="calculatedFees" class="text-body-default ml-auto">
                    {{ useFormatPrice(calculatedFees, true, quoteCurrency) }}
                  </p>
                </div>
                <div v-for="(service, i) in filteredExtraServices" :key="i" class="mb-3 ml-3">
                  <div class="mb-1.5 flex gap-6">
                    <p class="text-body-default-m">{{ service.title }}</p>
                    <p class="text-body-default ml-auto">
                      {{ useFormatPrice(service.price, true, quoteCurrency) }}
                    </p>
                  </div>
                  <div v-for="(value, ix) in service.values" :key="ix">
                    <div v-if="value.line" class="mb-1.5 flex gap-3">
                      <p v-for="fieldValue in value.values" :key="fieldValue" class="text-body-small">
                        {{ fieldValue }}
                      </p>
                    </div>
                    <div v-if="!value.line && value.value" class="mb-1 flex gap-3">
                      <p class="text-body-small">{{ value.label }}:</p>
                      <p class="text-body-small">{{ value.value }}</p>
                    </div>
                    <p v-else class="text-body-small">{{ value.label }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-auto">
            <Divider class="my-0" />
            <div class="flex p-3">
              <p v-if="isDFM && carrierIsDfm" class="text-body-large-m">
                {{ t("provisional_price") }}
              </p>
              <p v-else class="text-body-large-m">{{ t("total") }}</p>
              <p class="text-body-large ml-auto font-medium">
                {{
                  useFormatPrice(
                    calculatedTotalPrice ?? parseFloat(values.carrier_product_price) + getInsurancePrice,
                    true,
                    quoteCurrency
                  )
                }}
              </p>
            </div>
          </div>
        </div>
        <div class="bg-bgr-secondary mb-2 flex h-max w-full items-center gap-4 rounded-xl px-3 py-2">
          <p class="text-body-small min-w-max font-medium">{{ t("reference_id") }}</p>
          <InputText
            id="reference"
            class="ml-auto w-full max-w-[255px]"
            name="reference"
            :placeholder="t('reference_ex')"
          />
        </div>
        <div class="bg-bgr-secondary mb-2 flex h-max w-full gap-1 rounded-xl px-3 py-2">
          <p class="text-body-small mt-1.5 min-w-max font-medium">{{ t("remark") }}</p>
          <Tooltip class="pt-1.5" :text="t('remark_tooltip')">
            <Icon src="info" :size="ComponentSize.default" />
          </Tooltip>
          <InputText id="comment" class="ml-auto w-full max-w-[255px]" name="comment" placeholder="" :max="250" />
        </div>
        <div class="bg-bgr-secondary flex h-max w-full gap-2.5 rounded-xl px-3 py-3">
          <TickerCheckbox id="accept_terms" name="accept_terms" />
          <p v-if="isDFM" class="text-body-default">
            {{ t("i_accept") }}
            <span class="text-fgr-info cursor-pointer">
              <a
                href="https://www.fragt.dk/media/2033/forretningsbetingelser_transport_2021.pdf"
                target="_blank"
                rel="noopener noreferrer"
                >{{ t("the_terms").toLocaleLowerCase() }}</a
              ></span
            >
          </p>
          <p v-else class="text-body-default">
            {{ t("i_accept") }}
            <span class="text-fgr-info cursor-pointer">
              <a href="https://www.homerunner.com/handelsbetingelser/" target="_blank" rel="noopener noreferrer">{{
                t("the_terms").toLocaleLowerCase()
              }}</a></span
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { CarrierOption } from "~/types/shipment";

  import { loading } from "../../helpers/service";

  const { t } = useI18n();
  const emit = defineEmits(["changeStep", "updatePrice"]);

  const props = defineProps<{
    values: object;
    mode: string;
    services: object;
  }>();

  const extraServices = computed(() => {
    if (!props.values?.services) return false;
    return Object.keys(props.values.services).length !== 0;
  });

  const { isDFM, isSKYNET } = useWorkspacesStore();
  const { fetchInsurances } = useInsurance();
  const freeCoverage = ref(0);
  const freeCoverageCurrency = ref<string | null>(null);
  const calculatedTotalPrice = ref(null);
  const calculatedFees = ref(null);
  const calculatedPrice = ref(null);
  const feesDetails = ref<any[]>();

  onMounted(() => {
    // Nothing picked in the insurance step - look up the carrier's included cover to show instead.
    if (!props.values.insurance_coverage) {
      fetchInsurances(props.values, quoteCurrency.value).then((res) => {
        if (!res?.length) return;
        // First entry is the carrier's included cover, labelled with its own currency.
        freeCoverage.value = res[0].coverage;
        freeCoverageCurrency.value = res[0].currency;
      });
    }
    if (isDFM && carrierIsDfm.value) {
      getPrice();
    }
    if (isDFM && !carrierIsDfm.value && props.mode !== "return") {
      globalLoaderToggle();
      homeFetch("customers/active-transfer").then((res) => {
        globalLoaderToggle();
        if (res.data) {
          return;
        } else {
          EventBus.$emit("NEWSHIPMENT_NO_TRANSFER");
        }
      });
    }
  });

  const formatExtraservices = (values: object) => {
    const extraservices = {};
    extraservices.service_codes = [];
    Object.entries(values).forEach(([key, value]) => {
      if (!value.isSet) {
        return;
      }
      if (/^([A-Z0-9]{3})$/.test(key)) {
        extraservices.service_codes.push(key);
        return;
      }
      if (value.array) {
        extraservices[key] = value.array;
        return;
      } else {
        extraservices[key] = value;
        return;
      }
    });
    return extraservices;
  };

  const getPrice = async () => {
    loading.value = true;
    const services = formatExtraservices(props.values.services);
    const cps = props.values.carrier_product_service.split("_");
    const [carrier, carrier_product, ...carrier_service] = cps;
    const data = {
      shipment: {
        sender: {
          ...props.values.sender,
          country: props.values.sender.country.toUpperCase(),
          type: props.values.sender.business ? "business" : "private",
        },
        receiver: {
          ...props.values.receiver,
          country: props.values.receiver.country.toUpperCase(),
          type: props.values.receiver.business ? "business" : "private",
        },
        height: props.values.height,
        length: props.values.length,
        width: props.values.width,
        weight: props.values.weight,
        carrier,
        carrier_product,
        carrier_service: carrier_service.join("_"),
        servicepoint_id: props.values?.droppoint,
        services: services,
      },
    };
    const price = await homeFetch("shipments/calculate-price", {
      method: "POST",
      body: data,
    })
      .then((response) => {
        return response.data;
      })
      .finally(() => {
        loading.value = false;
      });
    calculatedPrice.value = price.price + price.price_surcharge + price.price_capacity_surcharge;
    calculatedFees.value = price.fees;
    calculatedTotalPrice.value = price.total_price;
    feesDetails.value = Array.isArray(price.fees_details) ? price.fees_details : [];

    emit("updatePrice", price);
    loading.value = false;
  };

  const filteredExtraServices = computed(() => {
    return transformExtraServicesForPresentation(
      props.services as CarrierOption[],
      props.values?.services,
      feesDetails.value
    );
  });

  const getInsurancePrice = computed(() => {
    return props.values.insurance ? parseFloat(props.values.insurance_price) : 0;
  });

  /** Currency of the carrier quote - everything on this summary is priced in it. */
  const quoteCurrency = computed<string | null>(() => props.values.carrier_product_currency || null);

  /**
   * Currency the selected insurance's amounts were converted to, as labelled by the API.
   * Falls back to the quote currency, never to a hardcoded DKK.
   */
  const insuranceCurrency = computed<string | null>(() => props.values.insurance_currency || quoteCurrency.value);

  const canChangeSender = computed(() => {
    if (!isDFM) return true;
    return props.mode === "return";
  });

  const canChangeReceiver = computed(() => {
    if (!isDFM) return true;
    return props.mode === "new";
  });

  const carrier = computed(() => {
    return props.values.carrier_product_service.split("_")[0];
  });

  const carrierIsDfm = computed(() => {
    return carrier.value === "dfm";
  });
</script>
